import '@assets/fonts/DMSerifText/DMSerifText.css';
import '@assets/fonts/National/National.css';
import '@assets/fonts/OpenSans/Open-Sans.css';
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Checkbox,
  colorsTuple,
  createTheme,
  Input,
  Menu,
  Pagination,
  Paper,
  SegmentedControl,
  Select,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import classes from './theme.module.css';

export const themeConfig = createTheme({
  cursorType: 'pointer',
  fontFamily: 'Open Sans, sans-serif',

  headings: {
    fontFamily: 'Open Sans, sans-serif',
    fontWeight: '600',
  },

  primaryColor: 'brand-primary', //primary color must not end with index (e.g brand-primary-1)
  defaultRadius: 'md',

  colors: {
    'brand-black': colorsTuple('#333333'),
    'brand-primary': colorsTuple('#28537D'),

    'brand-primary-1': colorsTuple('#28537D'),
    'brand-primary-2': colorsTuple('#5088C6'),
    'brand-primary-3': colorsTuple('#46BBD4'),

    'brand-secondary-1': colorsTuple('#0497A6'),
    'brand-secondary-2': colorsTuple('#23AE7A'),
    'brand-secondary-3': colorsTuple('#EB6A33'),
    'brand-secondary-4': colorsTuple('#DD9205'),
    'brand-secondary-5': colorsTuple('#661BCE'),
    'brand-secondary-6': colorsTuple('#AEC211'),

    'brand-chart-1': colorsTuple('#E67E22'),
    'brand-chart-2': colorsTuple('#E8900C'),
    'brand-chart-3': colorsTuple('#F0B27A'),
    'brand-chart-4': colorsTuple('#2980B9'),
    'brand-chart-5': colorsTuple('#17A589'),
    'brand-chart-6': colorsTuple('#45B39D'),
    'brand-chart-7': colorsTuple('#7D3C98'),
    'brand-chart-8': colorsTuple('#E8723A'),
    'brand-chart-9': colorsTuple('#C4943F'),
    'brand-chart-10': colorsTuple('#7A9E4E'),
    'brand-chart-11': colorsTuple('#4A8C8C'),
    'brand-chart-12': colorsTuple('#3C5A7A'),

    'brand-grey-1': colorsTuple('#F7F7F7'),
    'brand-grey-2': colorsTuple('#E8E7E7'),
    'brand-grey-3': colorsTuple('#888888'),
    'brand-grey-4': colorsTuple('#555555'),

    'brand-border-grey': colorsTuple('#E8E7E7'),
    'brand-tooltip': colorsTuple('#333333E5'),

    'brand-danger': colorsTuple('#C21020'),

    'status-info': colorsTuple('#E4F2FF'),
    'status-warning': colorsTuple('#FEF5E4'),
    'status-danger': colorsTuple('#FFF4F5'),
    'status-success': colorsTuple('#E7F9DA'),
  },

  spacing: {
    '2xl': '40px',
  },

  components: {
    ActionIcon: ActionIcon.extend({
      defaultProps: {
        variant: 'default',
        bd: 0,
        bdrs: 2,
      },
      classNames: {
        root: classes.actionIcon,
      },
    }),

    Tooltip: Tooltip.extend({
      defaultProps: {
        color: 'brand-tooltip',
        radius: 2,
        openDelay: 300,
        arrowOffset: 50,
        arrowSize: 8,
        withArrow: true,
      },
    }),

    Anchor: Anchor.extend({
      defaultProps: {
        underline: 'never',
        c: 'brand-primary-1',
      },
    }),

    Input: Input.extend({
      defaultProps: {
        radius: 'sm',
      },
      classNames: { input: classes.input },
    }),

    Checkbox: Checkbox.extend({
      defaultProps: {
        radius: 'sm',
      },
    }),

    DatePickerInput: DatePickerInput.extend({
      classNames: {
        input: classes.dateInput,
        day: classes.dateInputDay,
      },
    }),

    Title: Title.extend({
      defaultProps: {
        c: 'brand-black',
      },
      classNames: { root: classes.title },
    }),

    Text: Text.extend({
      defaultProps: {
        variant: 'regular16',
        c: 'brand-black',
      },
      classNames: { root: classes.text },
    }),

    TextInput: TextInput.extend({
      defaultProps: {
        radius: 'sm',
      },
      classNames: {
        label: classes.textInputLabel,
        input: classes.textInput,
      },
    }),

    Button: Button.extend({
      defaultProps: {
        variant: 'filled',
        h: '2.5rem',
        px: '1.25rem',
        py: '10px',
      },
      classNames: {
        root: classes.button,
        label: classes.buttonLabel,
      },
    }),

    Badge: Badge.extend({
      defaultProps: {
        fz: '16px',
        lh: '16px',
        fw: '400',
      },
      classNames: {
        root: classes.badge,
      },
    }),

    Paper: Paper.extend({
      classNames: {
        root: classes.paper,
      },
    }),

    Pagination: Pagination.extend({
      classNames: {
        control: classes.pagination,
      },

      defaultProps: {
        radius: 'xs',
        size: 'md',
        withEdges: true,
        gap: 5,
      },
    }),

    Select: Select.extend({
      classNames: {
        input: classes.select,
        option: classes.selectOption,
      },
      defaultProps: {
        checkIconPosition: 'right',
      },
    }),

    SegmentedControl: SegmentedControl.extend({
      defaultProps: {
        withItemsBorders: false,
        color: 'brand-primary-1',
        bg: 'brand-grey-1',
        p: 0,
      },
      classNames: {
        root: classes.segmentedControl,
        label: classes.segmentedControlLabel,
      },
    }),

    Table: Table.extend({
      classNames: {
        table: classes.table,
      },
    }),

    Menu: Menu.extend({
      classNames: {
        dropdown: classes.menuDropdown,
        item: classes.menuItem,
      },
    }),
  },
});
